"use client"

import { useState } from "react"
import { FaTrash } from 'react-icons/fa';
import styles from "./css/obstetrics.module.css"
import { updateConsultation } from "../../../../api/clinic/consultationApi.js";

export default function Obstetrics({ 
  consultationId, 
  patientData = {}, 
  initialData,
  onDataChange,
  ensureConsultationExists,
  onSave 
}) {
  const [obstetricRecords, setObstetricRecords] = useState(
    initialData?.obstetricRecords || [{ description: "" }]
  )
  const [surgeryRecords, setSurgeryRecords] = useState(
    initialData?.surgeryRecords || [{ surgery: "", date: "" }]
  )
  const [pare, setPare] = useState(initialData?.pare || "")
  const [gravida, setGravida] = useState(initialData?.gravida || "")
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [saveSuccess, setSaveSuccess] = useState(false)
  
  const notifyChange = () => {
    if (onDataChange) onDataChange()
  }

  const addObstetricRecord = () => {
    setObstetricRecords([...obstetricRecords, { description: "" }])
    notifyChange()
  }

  const removeObstetricRecord = (index) => {
    setObstetricRecords(obstetricRecords.filter((_, i) => i !== index))
    notifyChange()
  }

  const addSurgeryRecord = () => {
    setSurgeryRecords([...surgeryRecords, { surgery: "", date: "" }])
    notifyChange()
  }

  const removeSurgeryRecord = (index) => {
    setSurgeryRecords(surgeryRecords.filter((_, i) => i !== index))
    notifyChange()
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError("")
      setSaveSuccess(false)

      // Ensure consultation exists (creates if needed)
      const activeConsultationId = await ensureConsultationExists()

      // Filter out empty entries and prepare data as arrays
      const validObstetrics = obstetricRecords.filter(r => r.description.trim())
      const validSurgery = surgeryRecords.filter(r => r.surgery.trim() || r.date)

      const payload = {
        icd10description: validObstetrics.map(r => r.description),
        surgery: validSurgery.map(r => r.surgery),
        dateofsurgery: validSurgery.map(r => r.date ? new Date(r.date).toISOString() : null).filter(d => d),
        para: pare ? [parseInt(pare)] : [],
        gravida: gravida ? [parseInt(gravida)] : [],
      }

      await updateConsultation(activeConsultationId, payload)

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      
      if (onSave) {
        onSave({ obstetricRecords, surgeryRecords, pare, gravida })
      }
    } catch (e) {
      setSaveError(e.message || "Failed to save data")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.sectionsContainer}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Obstetrics</h3>
              <button className={styles.addButton} onClick={addObstetricRecord}>
                +
              </button>
            </div>

            <div className={styles.tableHeader}>
              <span className={styles.headerCell}>ICD 10 Description</span>
            </div>

            {obstetricRecords.map((record, index) => (
              <div key={index} className={styles.tableRow}>
                <input
                  type="text"
                  className={styles.input}
                  value={record.description}
                  onChange={(e) => {
                    const newRecords = [...obstetricRecords]
                    newRecords[index].description = e.target.value
                    setObstetricRecords(newRecords)
                    notifyChange()
                  }}
                />
                <button className={styles.deleteButton} onClick={() => removeObstetricRecord(index)}>
                  <FaTrash color="red" />
                </button>
              </div>
            ))}
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Surgery</h3>
              <button className={styles.addButton} onClick={addSurgeryRecord}>
                +
              </button>
            </div>

            <div className={styles.tableHeader}>
              <span className={styles.headerCell}>Surgery</span>
              <span className={styles.headerCell}>Date</span>
            </div>

            {surgeryRecords.map((record, index) => (
              <div key={index} className={styles.tableRow}>
                <input
                  type="text"
                  className={styles.input}
                  value={record.surgery}
                  onChange={(e) => {
                    const newRecords = [...surgeryRecords]
                    newRecords[index].surgery = e.target.value
                    setSurgeryRecords(newRecords)
                    notifyChange()
                  }}
                />
                <input
                  type="date"
                  className={styles.input}
                  value={record.date}
                  onChange={(e) => {
                    const newRecords = [...surgeryRecords]
                    newRecords[index].date = e.target.value
                    setSurgeryRecords(newRecords)
                    notifyChange()
                  }}
                />
                <button className={styles.deleteButton} onClick={() => removeSurgeryRecord(index)}>
                  <FaTrash color="red" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.pregnancyInfo}>
          <div className={styles.pregnancyField}>
            <label className={styles.pregnancyLabel}>Pare:</label>
            <input
              type="text"
              className={styles.pregnancyInput}
              value={pare}
              onChange={(e) => { setPare(e.target.value); notifyChange(); }}
            />
          </div>
          <div className={styles.pregnancyField}>
            <label className={styles.pregnancyLabel}>Gravida:</label>
            <input
              type="text"
              className={styles.pregnancyInput}
              value={gravida}
              onChange={(e) => { setGravida(e.target.value); notifyChange(); }}
            />
          </div>
        </div>
      </div>

      {saveError && (
        <div className={styles.inlineError}>
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className={styles.successMessage}>
          Data saved successfully!
        </div>
      )}

      <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  )
}